'use client';

import { NoDataFound } from '@pickleballinc/ui/components/global/NoDataFound';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import { useQuery } from '@tanstack/react-query';
import { permanentRedirect, useSearchParams } from 'next/navigation';

import { MemeArticleCard } from '@/modules/blog/article_components/MemeArticleCard';
import { PictureArticleCard } from '@/modules/blog/article_components/PictureArticleCard';
import { SkeletonMemeArticleCard } from '@/modules/blog/article_components/SkeletonMemeArticleCard';
import { SkeletonPictureArticleCard } from '@/modules/blog/article_components/SkeletonPictureArticleCard';
import { VideoArticleCard } from '@/modules/blog/article_components/VideoArticleCard';
import { BlogPagination } from '@/modules/blog/components/BlogPagination';
import { MEME_SUBCATEGORY_SLUGS, PAGE_SIZE } from '@/modules/blog/constants';
import { Category } from '@/types/blog.types';
import { getArticles } from '@/utils/actions/blog/getArticles';
import { getWithBaseUrl } from '@/utils/helpers/getWithBaseUrl';

interface PaginationContentProps {
  categorySlug: string;
  currentPage: string;
  category: Category;
}

export const PaginationMemeContent = ({ categorySlug, currentPage }: PaginationContentProps) => {
  const searchParams = useSearchParams();
  const subCategory = searchParams?.get('subcategory') || '';

  if (subCategory === '') {
    const memesUrl = getWithBaseUrl(
      `/${categorySlug}/1?subcategory=${MEME_SUBCATEGORY_SLUGS.Meme}`
    );
    permanentRedirect(memesUrl);
  }

  const { data, isFetching } = useQuery({
    queryKey: ['articles', categorySlug, currentPage, subCategory],
    queryFn: async () =>
      await getArticles({
        slug: categorySlug,
        subCategorySlug: subCategory,
        page: Number(currentPage),
      }),
    keepPreviousData: true,
  });

  const articles = data?.data?.articles || [];
  const totalPages = data?.data?.totalRecords ? Math.ceil(data.data.totalRecords / PAGE_SIZE) : 1;

  return articles.length === 0 && !isFetching ? (
    <div className='w-full max-w-screen-sm p-4 mx-auto'>
      <NoDataFound
        title='No articles found!'
        subtitle='There are no articles in this subcategory.'
      />
    </div>
  ) : (
    <div className='pt-2 sm:pt-4'>
      {subCategory === MEME_SUBCATEGORY_SLUGS.Meme ? (
        <div className='columns-1 sm:columns-2 gap-10'>
          {isFetching
            ? Array.from({ length: 6 }).map((_, index) => (
                <div className='mb-4' key={index}>
                  <SkeletonMemeArticleCard />
                </div>
              ))
            : articles.map((article) => (
                <CustomLink
                  href={`/${categorySlug}/${article.slug}`}
                  key={`article-${article.slug}`}
                  className='break-inside-avoid mb-4 block'
                >
                  <MemeArticleCard
                    imageUrl={article.imageUrl}
                    alt={article.imageAltText}
                    title={article.title}
                    authorFullName={`${article.authorFullName}`}
                    type='paginationCover'
                    publishedAt={article.publishDateDisplayed}
                    description={article.description}
                  />
                </CustomLink>
              ))}
        </div>
      ) : (
        <div className='mb-4 grid justify-center gap-4 sm:gap-y-10 grid-cols-1 sm:grid-cols-1'>
          {isFetching
            ? Array.from({ length: 6 }).map((_, index) => (
                <SkeletonPictureArticleCard key={index} />
              ))
            : articles.map((article) =>
                subCategory === MEME_SUBCATEGORY_SLUGS.Photo ? (
                  <CustomLink
                    href={`/${categorySlug}/${article.slug}`}
                    key={`article-${article.slug}`}
                    className='flex size-full min-h-[125px] sm:min-h-[245px] justify-center'
                  >
                    <PictureArticleCard
                      imageUrl={article.imageUrl}
                      alt={article.imageAltText}
                      title={article.title}
                      authorFullName={`${article.authorFullName}`}
                      type='paginationCover'
                      publishedAt={article.publishDateDisplayed}
                      description={article.description}
                    />
                  </CustomLink>
                ) : (
                  <div
                    className='flex w-full max-h-[245px] justify-center'
                    key={`article-${article.slug}`}
                  >
                    <VideoArticleCard
                      videoUrl={`${article.videoUrl}`}
                      title={article.title}
                      authorFullName={`${article.authorFullName}`}
                      type='paginationCover'
                      publishedAt={article.publishDateDisplayed}
                      description={article.description}
                      link={`/${categorySlug}/${article.slug}`}
                      subcategoryTitle={
                        subCategory === MEME_SUBCATEGORY_SLUGS.Podcast ? 'Podcast' : 'Video'
                      }
                    />
                  </div>
                )
              )}
        </div>
      )}

      {articles.length > 0 && (
        <div className='mb-4 max-w-[813px] sm:mt-4 sm:px-4'>
          <BlogPagination
            currentPage={currentPage}
            totalPages={totalPages}
            categorySlug={categorySlug}
            subCategory={subCategory}
          />
        </div>
      )}
    </div>
  );
};
